import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../redux/hooks.ts";

import { TenantReport } from "@multiburo/ui-components";
import CryptoJS from "crypto-js";
import api from "../../../../api";
import { CoreLoading } from "../../../../components/common/CoreLoading.tsx";
import { useBreadcrumb } from "../../../../hooks/useBreadcrumb.tsx";
import { Breadcrumb } from "../../../../components/common/Breadcrumb.tsx";

const getTenantReportWebData = async (folderNumber: string) => {
  try {
    const { data } = await api.get(
      `/docvault/folders/${folderNumber}/folio/preview`,
    );

    // Check for encryption errors first
    if (data.error === "ENCRYPTION_FAILED") {
      throw new Error("Server encryption failed. Please contact support.");
    }

    // Check if we have encrypted data
    if (data.data) {
      try {
        const key = import.meta.env.VITE_APP_TENANT_REPORT_AES_KEY;

        if (!key) {
          throw new Error(
            "AES decryption key not found in environment variables",
          );
        }

        // Decode base64
        const encryptedBytes = CryptoJS.enc.Base64.parse(data.data);

        // Extract IV (first 16 bytes) and ciphertext (rest)
        const iv = CryptoJS.lib.WordArray.create(
          encryptedBytes.words.slice(0, 4),
        );
        const ciphertext = CryptoJS.lib.WordArray.create(
          encryptedBytes.words.slice(4),
        );

        // Decrypt - Fix: Create proper CipherParams object
        const decrypted = CryptoJS.AES.decrypt(
          CryptoJS.lib.CipherParams.create({ ciphertext: ciphertext }),
          CryptoJS.enc.Utf8.parse(key.substring(0, 32)), // Ensure 32 chars
          {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
          },
        );

        const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

        if (!decryptedText || decryptedText.trim().length === 0) {
          throw new Error("Decrypted text is empty");
        }

        return JSON.parse(decryptedText);
      } catch (decryptionError) {
        console.error("AES decryption failed:", decryptionError);
        // Fix: Type guard for error handling
        const errorMessage =
          decryptionError instanceof Error
            ? decryptionError.message
            : "Unknown decryption error";
        throw new Error(`Failed to decrypt report data: ${errorMessage}`);
      }
    }

    // If we get here, something unexpected happened
    throw new Error("No encrypted data received from server");
  } catch (error) {
    console.error("Error fetching tenant report data:", error);
    throw error;
  }
};

export default function MultireportPreviewPage() {
  const params = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Fix: Type the error state properly
  const [error, setError] = useState<Error | null>(null);

  const { userInfo } = useAppSelector((state) => state.auth);
  const groups = userInfo?.groups ?? [];
  const isAdminOrOps =
    groups.includes("admin") || groups.includes("operations");
  const isAssistantUser = groups.includes("assistant");
  const isCjaUser = groups.includes("cja");
  // Route-level access should mirror the card icons
  const canAccess = isAdminOrOps || isAssistantUser || isCjaUser;

  const folderNumber = params.folderNumber;
  const breadcrumbItems = useBreadcrumb(folderNumber);

  useEffect(() => {
    if (!canAccess) return;

    const loadTenantReport = async () => {
      try {
        setLoading(true);
        // Fix: Add type guard for folderNumber
        if (!folderNumber) {
          throw new Error("Folder number is required");
        }
        const tenantReportData = await getTenantReportWebData(folderNumber);
        setData(tenantReportData);
      } catch (err) {
        console.error("Failed to fetch tenant report:", err);
        // Fix: Type guard for error setting
        const error =
          err instanceof Error ? err : new Error("Unknown error occurred");
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    loadTenantReport();
  }, [canAccess, folderNumber]); // Add folderNumber to dependencies

  if (!canAccess) {
    return (
      <div className="w-full h-full p-8">
        <Breadcrumb items={breadcrumbItems} />
        <div>
          Accesso denegado. No tienes permisos para previsualizar este reporte.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="w-full h-full p-8">
        <Breadcrumb items={breadcrumbItems} />
        <CoreLoading size={2} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full p-8">
        <Breadcrumb items={breadcrumbItems} />
        <div>Error loading report: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-8">
      <Breadcrumb items={breadcrumbItems} />
      {data ? <TenantReport data={data} /> : <div>No data available</div>}
    </div>
  );
}
